import { db, type CacheRow } from './localDb.js'
import { enqueue } from './outbox.js'
import { uuidv7 } from './ids.js'
import { syncNow } from './syncEngine.js'
import { api } from './apiClient.js'

/**
 * Schreibende Aktionen auf Stammdaten.
 *
 * Alle drei sind Klasse A: sie gehen in den Ausgangskorb, sind sofort
 * quittiert und funktionieren auch im Flugmodus. Die Kennung erzeugt der
 * Client, damit ein Projekt schon auf einen Kunden verweisen kann, der noch
 * gar nicht übertragen ist.
 *
 * Der Cache wird optimistisch mitgeschrieben – sonst verschwindet ein offline
 * angelegter Kunde aus der Liste, sobald der Bildschirm neu zeichnet.
 */

export interface Actor {
  tenantId: string
  userId: string
}

async function writeLocal(table: string, row: CacheRow): Promise<void> {
  await db.table(table).put(row)
}

// ---------------------------------------------------------------------------
// Kunden
// ---------------------------------------------------------------------------

export interface CustomerFields {
  name: string
  contact?: string | null
  street?: string | null
  zip?: string | null
  city?: string | null
  email?: string | null
  phone?: string | null
  notes?: string | null
  oneOff?: boolean
}

export async function createCustomer(actor: Actor, input: CustomerFields): Promise<string> {
  const id = uuidv7()
  const payload = { id, ...input, oneOff: input.oneOff ?? false }
  await writeLocal('customers', { ...payload, version: 1, changeSeq: 0, pending: true } as CacheRow)
  await enqueue({
    type: 'customer.create',
    actionClass: 'A',
    method: 'POST',
    path: '/customers',
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
  return id
}

export async function updateCustomer(
  actor: Actor,
  id: string,
  input: CustomerFields,
  expectedVersion: number
): Promise<void> {
  const payload = { ...input, oneOff: input.oneOff ?? false, expectedVersion }
  const existing = await db.customers.get(id)
  if (existing) await writeLocal('customers', { ...existing, ...payload, pending: true })
  await enqueue({
    type: 'customer.update',
    actionClass: 'A',
    method: 'PATCH',
    path: `/customers/${id}`,
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
}

// ---------------------------------------------------------------------------
// Projekte
// ---------------------------------------------------------------------------

export interface ProjectFields {
  customerId: string
  name: string
  street?: string | null
  zip?: string | null
  city?: string | null
  notes?: string | null
  status?: 'offen' | 'abgeschlossen'
}

export async function createProject(actor: Actor, input: ProjectFields): Promise<string> {
  const id = uuidv7()
  const payload = { id, ...input }
  await writeLocal('projects', {
    ...payload,
    status: input.status ?? 'offen',
    version: 1,
    changeSeq: 0,
    pending: true
  } as CacheRow)
  await enqueue({
    type: 'project.create',
    actionClass: 'A',
    method: 'POST',
    path: '/projects',
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
  return id
}

export async function updateProject(
  actor: Actor,
  id: string,
  input: ProjectFields,
  expectedVersion: number
): Promise<void> {
  const payload = { ...input, expectedVersion }
  const existing = await db.projects.get(id)
  if (existing) await writeLocal('projects', { ...existing, ...payload, pending: true })
  await enqueue({
    type: 'project.update',
    actionClass: 'A',
    method: 'PATCH',
    path: `/projects/${id}`,
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
}

export async function setProjectMembers(
  actor: Actor,
  projectId: string,
  userIds: string[]
): Promise<void> {
  await db.projectMembers.where('projectId').equals(projectId).delete()
  for (const userId of userIds) {
    await db.projectMembers.put({ id: `${projectId}:${userId}`, projectId, userId })
  }
  await enqueue({
    type: 'project.members.set',
    actionClass: 'A',
    method: 'PUT',
    path: `/projects/${projectId}/members`,
    payload: { userIds },
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
}

// ---------------------------------------------------------------------------
// Leistungskatalog
// ---------------------------------------------------------------------------

export interface CatalogFields {
  code: string
  name: string
  unit: string
  unitPriceRp: number
  vatRate: number
  kind: 'arbeit' | 'material'
  active?: boolean
}

export async function createCatalogItem(actor: Actor, input: CatalogFields): Promise<string> {
  const id = uuidv7()
  const payload = { id, ...input }
  await writeLocal('catalogItems', {
    ...payload,
    active: input.active ?? true,
    version: 1,
    changeSeq: 0,
    pending: true
  } as CacheRow)
  await enqueue({
    type: 'catalog.item.create',
    actionClass: 'A',
    method: 'POST',
    path: '/catalog-items',
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
  return id
}

export async function updateCatalogItem(
  actor: Actor,
  id: string,
  input: CatalogFields,
  expectedVersion: number
): Promise<void> {
  const payload = { ...input, expectedVersion }
  const existing = await db.catalogItems.get(id)
  if (existing) await writeLocal('catalogItems', { ...existing, ...payload, pending: true })
  await enqueue({
    type: 'catalog.item.update',
    actionClass: 'A',
    method: 'PATCH',
    path: `/catalog-items/${id}`,
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
}

// ---------------------------------------------------------------------------
// Löschen oder archivieren
// ---------------------------------------------------------------------------

export interface DisposalResult {
  id: string
  disposal: 'geloescht' | 'archiviert'
  usage: Array<{ was: string; anzahl: number }>
  message: string
}

/**
 * Klasse B, und zwar aus einem inhaltlichen Grund.
 *
 * Ob gelöscht oder archiviert wird, weiss nur der Server – er allein kennt
 * die Belege. Ein optimistisches "ist weg" wäre in der Hälfte der Fälle
 * falsch, und der Benutzer sähe einen Datensatz verschwinden, der gleich
 * wieder auftaucht. Also: warten, dann anzeigen, was wirklich passiert ist.
 */
async function entsorge(
  table: string,
  path: string,
  id: string
): Promise<DisposalResult> {
  const result = await api.remove<DisposalResult>(`${path}/${id}`, uuidv7())
  if (result.disposal === 'geloescht') {
    await db.table(table).delete(id)
  } else {
    const row = await db.table(table).get(id)
    if (row) {
      await db
        .table(table)
        .put({ ...row, active: false, status: table === 'projects' ? 'archiviert' : row.status })
    }
  }
  // Die Liste muss sich sofort neu zeichnen. Ohne diesen Anstoss bliebe der
  // gelöschte Eintrag stehen, bis der nächste Abgleich von selbst läuft – und
  // genau das sieht für den Benutzer aus, als hätte der Klick nicht gewirkt.
  void syncNow()
  return result
}

export async function removeCustomer(_actor: Actor, id: string): Promise<DisposalResult> {
  return entsorge('customers', '/customers', id)
}

export async function removeProject(_actor: Actor, id: string): Promise<DisposalResult> {
  return entsorge('projects', '/projects', id)
}

export async function removeCatalogItem(_actor: Actor, id: string): Promise<DisposalResult> {
  return entsorge('catalogItems', '/catalog-items', id)
}

// ---------------------------------------------------------------------------
// Einstellungen – Klasse B, weil sie den Nummernkreis berühren
// ---------------------------------------------------------------------------

export interface SettingsResult {
  settings: Record<string, unknown>
  nextInvoiceNumberClamped: boolean
}

/**
 * Einstellungen sind Klasse B: sie gehen nicht in den Ausgangskorb.
 *
 * Der Grund ist der Nummernkreis. Eine optimistisch quittierte Änderung, die
 * der Server später anders anwendet – etwa weil die Rechnungsnummer nur
 * vorwärts darf –, würde dem Superuser einen Stand zeigen, den es nie gab.
 * Deshalb wird hier auf die Antwort gewartet und erst danach geschrieben.
 */
export async function saveSettings(
  _actor: Actor,
  settings: Record<string, unknown>
): Promise<SettingsResult> {
  const result = await api.send<SettingsResult>('/settings', 'PATCH', settings, uuidv7())
  if (result.settings) {
    await db.settings.put({ id: 'tenant', ...result.settings } as CacheRow)
  }
  return result
}

/**
 * Logo setzen und entfernen – ebenfalls Klasse B.
 *
 * Ein Logo im Ausgangskorb wäre die falsche Zusage: der Server prüft erst
 * beim Empfang, ob die Datei wirklich ein Bild ist und klein genug. Das im
 * Flugmodus zu quittieren und zwei Stunden später zurückzunehmen, wäre genau
 * die Art Überraschung, die wir vermeiden.
 */
export async function setTenantLogo(
  _actor: Actor,
  image: string,
  filename: string
): Promise<Record<string, unknown>> {
  const result = await api.setLogo(image, filename, uuidv7())
  if (result.settings) {
    await db.settings.put({ id: 'tenant', ...result.settings } as CacheRow)
  }
  return result.settings
}

export async function removeTenantLogo(_actor: Actor): Promise<Record<string, unknown>> {
  const result = await api.removeLogo(uuidv7())
  if (result.settings) {
    await db.settings.put({ id: 'tenant', ...result.settings } as CacheRow)
  }
  return result.settings
}
